import { hasAnyPolicy, normalizePolicyList, normalizePolicyString } from './policies';

export const PARTICIPANT_SIDEBAR_ITEMS = [
  { value: 'missingitems', label: 'Missing Items' },
  { value: 'candidates', label: 'Candidate List Entry' },
  { value: 'coordinatedcare', label: 'Coordinated Care' },
  { value: 'outreach', label: 'Client Outreach' },
  { value: 'referencebrowser', label: 'Reference Browser' },
  { value: 'documentscans', label: 'Documents/Scans' },
  { value: 'enrollment', label: 'Enrollment' },
  { value: 'incidents', label: 'Incidents' },
  { value: 'legal', label: 'Legal' },
  { value: 'medical', label: 'Medical' },
  { value: 'notes', label: 'Notes' },
  { value: 'notifications', label: 'Notifications' },
  { value: 'disposition', label: 'Plan and Location' },
  { value: 'programs', label: 'Programs' },
  { value: 'scans', label: 'Scans' }
];

export const PARTICIPANT_PERMISSION_KEYS = PARTICIPANT_SIDEBAR_ITEMS.map((item) => item.value);

export const LEGACY_PARTICIPANT_PROGRAM_ACCESS_KEYS = [
  'diversionaccess',
  'fistaccess',
  'odrhousingaccess',
  'prgwmnaccess',
  'dshaccess',
  'leadaccess'
];

const PARTICIPANT_PERMISSION_KEY_SET = new Set(PARTICIPANT_PERMISSION_KEYS);

export const PARTICIPANT_BACKEND_GROUP_BY_KEY: Record<string, string> = {
  missingitems: 'program:participant:missingitems',
  candidates: 'program:participant:candidates',
  coordinatedcare: 'program:participant:coordinatedcare',
  outreach: 'program:participant:outreach',
  referencebrowser: 'program:participant:referencebrowser',
  documentscans: 'program:participant:documentscans',
  enrollment: 'program:participant:enrollment',
  incidents: 'program:participant:incidents',
  legal: 'program:participant:legal',
  medical: 'program:participant:medical',
  notes: 'program:participant:notes',
  notifications: 'program:participant:notifications',
  disposition: 'program:participant:disposition',
  programs: 'program:participant:programs',
  scans: 'program:participant:scans'
};

const LEGACY_PARTICIPANT_GENERATED_POLICIES = [
  'program:participant~read',
  'program:participant~read',
  'program:participant~read',
  'program:participant~read',
  'program:participant:coordinatedcare~full',
  'program:candidate~full',
  'program:participant:documentscans~full',
  'program:participant:scans~full',
  'program:participant:enrollment~full',
  'tools:reports:access~read',
  'program:participant:notifications~full'
];

export const isParticipantPermissionKey = (value: unknown) => {
  return PARTICIPANT_PERMISSION_KEY_SET.has(`${value || ''}`.trim().toLowerCase());
};

const PARTICIPANT_MANAGED_POLICY_KEYS = new Set(
  [
    ...Object.values(PARTICIPANT_BACKEND_GROUP_BY_KEY),
    ...LEGACY_PARTICIPANT_GENERATED_POLICIES
  ].map((policy) => normalizePolicyString(policy))
);

const isParticipantManagedPolicy = (value: unknown) => {
  const normalized = normalizePolicyString(value);
  return Boolean(normalized && PARTICIPANT_MANAGED_POLICY_KEYS.has(normalized));
};

export const hasExactPolicy = (policies: unknown, policy: string) => {
  const normalizedPolicy = normalizePolicyString(policy);
  if (!normalizedPolicy) {
    return false;
  }
  return normalizePolicyList(policies).includes(normalizedPolicy);
};

const normalizePolicyListPreservingCase = (values: string[]) => {
  const nextValues: string[] = [];
  const seen = new Set<string>();

  (Array.isArray(values) ? values : []).forEach((value) => {
    const trimmed = `${value || ''}`.trim();
    const normalized = normalizePolicyString(trimmed);
    if (!normalized || seen.has(normalized)) {
      return;
    }
    seen.add(normalized);
    nextValues.push(trimmed);
  });

  return nextValues;
};

export const getPoliciesForParticipantPermission = (permissionKey: string) => {
  const key = `${permissionKey || ''}`.trim().toLowerCase();
  const backendGroup = PARTICIPANT_BACKEND_GROUP_BY_KEY[key];
  if (!backendGroup) {
    return [];
  }

  return [backendGroup];
};

export const getParticipantPermissionAccessMap = () => {
  return PARTICIPANT_PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = getPoliciesForParticipantPermission(key);
    return acc;
  }, {} as Record<string, string[]>);
};

export const applyParticipantPermissionPolicies = (
  currentPolicies: string[],
  enabledParticipantPermissions: string[]
) => {
  const enabledKeys = new Set(
    enabledParticipantPermissions
      .map((value) => `${value || ''}`.trim().toLowerCase())
      .filter((value) => PARTICIPANT_PERMISSION_KEY_SET.has(value))
  );

  const nextPolicies = normalizePolicyListPreservingCase(currentPolicies)
    .filter((policy) => !isParticipantManagedPolicy(policy));

  enabledKeys.forEach((key) => {
    nextPolicies.push(...getPoliciesForParticipantPermission(key));
  });

  return Array.from(new Set(nextPolicies));
};

export const getParticipantPermissionStateFromPolicies = (
  policies: string[],
  fallbackAllEnabled = false
) => {
  const normalizedPolicies = normalizePolicyList(policies);

  return PARTICIPANT_PERMISSION_KEYS.map((permission) => {
    const backendGroup = PARTICIPANT_BACKEND_GROUP_BY_KEY[permission];
    const runtimePrimitive = permission === 'candidates'
      ? 'program:candidate~full'
      : `${backendGroup}~full`;
    const enabled = fallbackAllEnabled
      || hasExactPolicy(normalizedPolicies, backendGroup)
      || hasAnyPolicy(normalizedPolicies, [runtimePrimitive]);
    return {
      permission,
      enabled
    };
  });
};
